import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import AdminNavbar from 'components/Navbars/AdminNavbar'
import Select from 'components/Inputs/Select'
import CheckBox from 'components/CheckBox'
import MoneyInput from 'components/Inputs/MoneyInput'
import NumberInput from 'components/Inputs/NumberInput'
import AlertDialog from 'components/AlertDialog'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import serviceValidator from 'validators/Admin/serviceValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'

import {
  Container,
  Form,
  FormField,
  Title,
  Label,
  Input,
  Row,
  InfoText,
  WarningText,
  ErrorContainer
} from './styles'

export default function EditService () {
  const [errorMessage, setErrorMessage] = useState (null)
  const [service, setService] = useState (null)
  const [categories, setCategories] = useState ([])
  const [loading, setLoading] = useState (true)
  const [showDeleteDialog, setShowDeleteDialog] = useState (false)
  const [showRestoreDialog, setShowRestoreDialog] = useState (false)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const token = AuthService.getToken ()
  const { id: service_id } = useParams ()
  const navigate = useNavigate ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [])

  const getData = async () => {
    try {
      const { data: categories } = await cutileiApi.get ('/service_categories', requestConfig)
      const { data: service } = await cutileiApi.get (`/services/${service_id}`, requestConfig)

      setService (service)
      setCategories (categories.map (category => ({
        label: category.name,
        value: category.id
      })))
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const deleteService = async () => {
    try {
      await cutileiApi.delete (`/services/${service_id}`, requestConfig)
      navigate ('/admin/config', { replace: true })
    } catch (error) {
      logApiError (error)
    }
  }

  const restoreService = async () => {
    try {
      await cutileiApi.post (`/services/${service_id}/reactivate`, {}, requestConfig)
      navigate ('/admin/config', { replace: true })
    } catch (error) {
      logApiError (error)
    }
  }

  const toggleDeleteDialog = () => setShowDeleteDialog (!showDeleteDialog)
  const toggleRestoreDialog = () => setShowRestoreDialog (!showRestoreDialog)
  const toggleOkDialog = () => setShowOkDialog (!showOkDialog)

  return (
    <Container>
      <AdminNavbar/>
      {loading ? <Loading/> : (
        <Formik
          validationSchema={serviceValidator}
          initialValues={{
            'number': service.number,
            'name': service.name,
            'description': service.description ?? '',
            'category': {
              label: service.category.name,
              value: service.category.id
            },
            'price': service.price,
            'cutileiService': service.cutilei_service
          }}
          onSubmit={async (values, { setSubmitting }) => {
            const { category, ...service } = values

            try {
              await cutileiApi.put (`/services/${service_id}`, {
                serviceCategoryId: category.value,
                ...service
              }, requestConfig)

              setSubmitting (false)
              toggleOkDialog ()
            } catch (error) {
              setSubmitting (false)
              logApiError (error)
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            handleChange,
            handleBlur,
            handleSubmit
          }) => (
            <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
              <Title>Editar serviço</Title>
              <Row>
                <FormField>
                  <Input 
                    placeholder='Nome'
                    value={values.name}
                    onChange={handleChange ('name')} 
                    onBlur={handleBlur ('name')}
                  />
                  {touched.name && errors.name && (
                    <WarningText>
                      {errors.name}
                    </WarningText>
                  )}
                </FormField>
                <FormField>
                  <Select
                    name='category'
                    placeholder='Categoria...'
                    value={values.category}
                    options={categories}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    error={errors.category}
                    touched={touched.category}
                  />
                  {touched.category && errors.category && (
                    <WarningText>
                      {errors.category}
                    </WarningText>
                  )}
                </FormField>
              </Row>
              <Row>
                <FormField>
                  <MoneyInput
                    name='price'
                    placeholder='Preço (R$)'
                    value={values.price}
                    onValueChange={setFieldValue}
                    onBlur={handleBlur ('price')}
                  />
                  {touched.price && errors.price && (
                    <WarningText>
                      {errors.price}
                    </WarningText>
                  )}
                </FormField>
                <FormField>
                  <NumberInput
                    name='number'
                    placeholder='Número na lista (opcional)'
                    value={values.number}
                    onChange={setFieldValue}
                    onBlur={handleBlur ('number')}
                  />
                  {touched.number && errors.number && (
                    <WarningText>
                      {errors.number}
                    </WarningText>
                  )}
                </FormField>
              </Row>
              <Row>
                <FormField>
                  <Input 
                    placeholder='Descrição do serviço'
                    value={values.description}
                    onChange={handleChange ('description')} 
                    onBlur={handleBlur ('description')}
                  />
                  {touched.description && errors.description && (
                    <WarningText>
                      {errors.description}
                    </WarningText>
                  )}
                </FormField>
              </Row>
              <Row>
                <CheckBox
                  id='cutileiService'
                  style={{marginRight: 6, marginTop: 3}}
                  value={values.cutileiService}
                  onValueChange={value => setFieldValue ('cutileiService', value)}
                />
                <Label htmlFor='cutileiService'>
                  Serviço Cutilei
                </Label>
              </Row>
              {errorMessage && (
                <ErrorContainer>
                  <InfoText>{errorMessage}</InfoText>
                </ErrorContainer>
              )}
              <MainButton
                label='Salvar alterações'
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
                width={180}
                style={{marginBottom: 10}}
              />
              {service.deleted ? (
                <SecondaryButton
                  label='Reativar serviço'
                  onClick={toggleRestoreDialog}
                  width={180}
                />
              ) : (
                <MainButton
                  label='Excluir serviço'
                  onClick={toggleDeleteDialog}
                  dangerous={true}
                  width={180}
                />
              )}
            </Form>
          )}
        </Formik>
      )}
      <AlertDialog
        visible={showDeleteDialog}
        title='Atenção!'
        message='Deseja realmente excluir este serviço?'
        confirmText='Sim'
        cancelText='Não'
        onConfirm={deleteService}
        onClose={toggleDeleteDialog}
        dangerous={true}
      />
      <AlertDialog
        visible={showRestoreDialog}
        title='Atenção!'
        message='Deseja realmente reativar este serviço?'
        confirmText='Sim'
        cancelText='Não'
        onConfirm={restoreService}
        onClose={toggleRestoreDialog}
      />
      <AlertDialog
        visible={showOkDialog}
        title='Sucesso'
        message='Alterações salvas!'
        confirmText='Ok'
        onConfirm={() => navigate ('/admin/config', { replace: true })}
        onClose={() => navigate ('/admin/config', { replace: true })}
      />
    </Container>
  )
}
